/**
 * useEnrichProgress - Hook de polling para acompanhar o progresso
 * do enriquecimento em massa com Gemini API.
 *
 * Usa o mesmo padrão do useHypeStatScraper: polling a cada 2s.
 */
import { useState, useCallback, useRef } from 'react';
import { getEnrichProgress, enrichPendingDomainsBatch } from '../../services/api';

const IDLE = { isRunning: false, current: 0, total: 0, domain: '', status: 'idle', successCount: 0, errorCount: 0, startedAt: null, finishedAt: null, errors: [] };

export const useEnrichProgress = (onComplete) => {
    const [progress, setProgress] = useState(IDLE);
    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const startPolling = useCallback(() => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            try {
                const res = await getEnrichProgress();
                setProgress(res.data);
                if (!res.data.isRunning) {
                    stopPolling();
                    if (onComplete) onComplete(res.data);
                }
            } catch {
                stopPolling();
            }
        }, 2000);
    }, [stopPolling, onComplete]);

    const startBatch = useCallback(async (domainIds) => {
        if (!domainIds?.length) return { success: false, error: 'Nenhum domínio selecionado' };
        setLoading(true);
        try {
            const res = await enrichPendingDomainsBatch(domainIds);
            if (res.data.success) {
                setProgress(res.data.data || { ...IDLE, status: 'running', total: domainIds.length, isRunning: true });
                startPolling();
                return { success: true };
            }
            return { success: false, error: res.data.message };
        } catch (err) {
            return { success: false, error: err.response?.data?.message || err.message };
        } finally {
            setLoading(false);
        }
    }, [startPolling]);

    const reset = useCallback(() => {
        stopPolling();
        setProgress(IDLE);
    }, [stopPolling]);

    return { progress, loading, startBatch, reset };
};
